import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { Subscription } from 'rxjs';

import { AppService } from '@app/services/app/app.service';
import { ProjectsEditorService, ProjectsService } from '@app/services/app/projects.service';
import { AccessProject, AccessProjectKeys } from '@app/models/access';

import { TitleService } from '@core/services/title.service';

import * as Objects from '@core/objects';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';
import { NotificationsService } from '@core/services/notifications/notifications.service';
import { WysiwygData } from '@core/models/wysiwyg-data.model';

@Component({
    selector: 'project-details-info-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class ProjectDetailsInfoMainComponent implements OnInit, OnDestroy{
    public readonly ACCESS_UPLOAD_FILE = "access_request_upload_file";
    public readonly ACCESS_EDIT_INFO = "access_request_edit_info";

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_ALLOWED = 2;
    public readonly STATUS_ERROR = 3;
    public readonly STATUS_NOT_FOUND = 4;

    public accessProject: AccessProject;
    public accessUpload: boolean = false;
    public accessEditInfo: boolean = false;
    public showUpload: boolean = false;
    public pageStatus: number = this.STATUS_LOADING;
    public actionInfoStatus: number = this.STATUS_DONE;
    public generalInfoView: boolean = true;

    public project: Objects.Request;

    public originVersion: Objects.Version = null;
    public menu: Array<ThreeDotsMenuItem> = [];
    protected menuDownload: ThreeDotsMenuItem = {
        title: "Download All Files",
        key: "download-all",
        icon: "download",
        order: 1
    }
    protected menuUpload: ThreeDotsMenuItem = {
            title: "Attach Files",
            key: "upload",
            icon: "upload-file",
            order: 2
        };    
    protected menuEditInfo: ThreeDotsMenuItem = {
            title: "Edit",
            key: "open-editor",
            icon: "edit", 
            order: 3
    };
    
    // editor controls
    public editorModules: any = [[{ 'list': 'ordered'}, { 'list': 'bullet' }]];
    
    protected subs = new Subscription();

    private generalInfoUpdated: WysiwygData;

	constructor(
        public app: AppService,
        private projectsSrv: ProjectsService,
        private editorSrv: ProjectsEditorService,
        private title: TitleService, 
        private notifSrv: NotificationsService
    ){}

	ngOnInit(){
        this.subs.add(this.editorSrv.selectAccessAll().subscribe((access: AccessProject) => {
            if (access != null){
                this.accessProject = access;
                this.accessUpload = this.accessProject.hasOwnProperty(AccessProjectKeys.UPLOAD_FILE) && this.accessProject[AccessProjectKeys.UPLOAD_FILE];
                this.accessEditInfo = this.accessProject.hasOwnProperty(AccessProjectKeys.EDIT_INFO) && this.accessProject[AccessProjectKeys.EDIT_INFO];
                this.updateMenu(this.menuUpload, this.accessUpload);
                this.updateMenu(this.menuEditInfo, this.accessEditInfo);
            }
        }));

        this.subs.add(this.projectsSrv.selectProjectFiles().subscribe((files: Objects.AmsFile[] ) => {
            if(files != null && files.length > 0){
                this.updateMenu(this.menuDownload);
            }
        }));
        this.subs.add(this.projectsSrv.selectProject().subscribe((project: Objects.Request ) => {
            if(project == null) return;

            this.setProject(project);
        }));
        this.subs.add(this.projectsSrv.selectProjectHeader().subscribe((header: Objects.BasicRequest) => {
            if (header == null) return;

            this.setProject(header as Objects.Request);
        }));
        this.subs.add(this.projectsSrv.selectError('set_project_info').subscribe((error: HttpErrorResponse) => this.setUpdateError(error)));

	}

    init() {
        this.title.set("Info - Project "+this.project.id);
    }

    protected setProject(project: Objects.Request) {
        this.project = project;
        this.init()

        this.pageStatus = this.STATUS_DONE;
    }

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    onUploadDone(){
        if(!this.accessUpload)
            return;

        this.showUpload = !this.showUpload;
    }

    onMenuClick(key: string) {
        switch(key) {
            case "open-editor":
                this.generalInfoView = !this.generalInfoView;
                break;
            case "download-all":
                // start notification to download compressed project files in zip
                this.notifSrv.addNotification(NotificationsContextEnum.EVENT_FILES_COMPRESSION_START, {id: this.project.id, details: { url: "/requests/"+this.project.id }});
                break;
            case "upload":
                // console.debug(" open upload click on files ", key)
                if(!this.accessUpload)
                    break;

                this.showUpload = !this.showUpload;
                break;
        }
    }
    // edition methods
    public onInfoChange(editorData: WysiwygData){

        // check for first change: when editor is different from original info
        if  (editorData != null && editorData.text.trim() != '' &&   
            editorData.html != this.project.info
        ){
           this.generalInfoUpdated = editorData;
        }
    }

    public isInfoReady(){
        if(this.accessEditInfo && this.generalInfoUpdated && this.generalInfoUpdated.text.trim() != '' ){
           return true;
        }

        return false;
    }

    public onInfoSubmit() {
        this.projectsSrv.setInfo(this.generalInfoUpdated.html);
        this.onInfoCancel();
    }

    public onInfoCancel() {
        this.generalInfoView = true;
    }

    protected updateMenu(item: ThreeDotsMenuItem, addItem:boolean = true) {
        this.menu = this.menu.filter((i: ThreeDotsMenuItem) => item.key != i.key);
        if(addItem){
            this.menu.push(item);
        }
    }

    // action when push modal buton when error
    onSetInfoError() {
        this.actionInfoStatus = this.STATUS_DONE;
    }

    protected setUpdateError(error: HttpErrorResponse) {

        if(error == null)
            return;

        switch(error.status) {
            case 500:
            case 400:
                this.actionInfoStatus = this.STATUS_ERROR;
                break;
            case 404:
                this.actionInfoStatus = this.STATUS_NOT_FOUND;
                break;
            case 403:
                this.actionInfoStatus = this.STATUS_NOT_ALLOWED;
                break;
        }
        // any update error wil reload the project to its real data.
        this.projectsSrv.load(this.project.id);
    }

}
